import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductIntialValues, ProductValidationSchema } from "../../constant";
import { useMutateEditProductVariations } from "../../hooks/useApiHook";
import { ErrorToast, SuccessToast } from "../../../../Utils/SweetAlert";
import InputWithLabel from "../../../../Components/InputWithLabel";
import "../../newstyle.css";
import { GetSingleProductVariation } from "../../../../Api/productVariations";
import { useQuery } from "@tanstack/react-query";
import BackButton from "../../../../Components/BackButton";
import SaveButton from "../../../../Components/SaveButton";

const EditProductVariations = () => {
  const navigate = useNavigate();
  const formRef = useRef();
  const stateData = useLocation();
  const { id } = stateData?.state || "";
  const [removeItem, setRemoveItem] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const editProductVariationsApi = useMutateEditProductVariations();

  const { data, refetch } = useQuery({
    queryKey: ["get-single-insight"],
    queryFn: () => GetSingleProductVariation({ id: id }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server error.");
    },
  });

  useEffect(() => {
    if (formRef?.current?.values) {
      formRef?.current?.setValues({
        name: data?.data?.name,
        variations: data?.data?.variationValues,
      });
    }
  }, [data]);

  const handleSubmitEditProductVariations = (values) => {
    const reqData = {
      id: id,
      name: values?.name,
      variations: values?.variations,
      removeItemIds: removeItem,
    };

    setIsLoading(true);
    editProductVariationsApi.mutate(reqData, {

      onSuccess: (response) => {
        if (response?.status != 0) {
          SuccessToast(response?.message || "Variation updated.");
          navigate("/admin/product-variations");
          refetch();
        } else {
          ErrorToast(response?.message || "Server Error.");
        }
        setIsLoading(false);
      },
      onError: (error) => {
        ErrorToast(error?.response?.data?.message || "Server error.");
        setIsLoading(false);
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Edit Product Variations</title>
      </Helmet>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card card-border overflow-visible">
                <div className="card-body">
                  <div className="page-header d-block">
                    <div className="row">
                      <div className="col">
                        <h3 className="page-title">Edit Product Variation</h3>
                      </div>
                      <div className="col-auto d-flex align-items-center justify-content-center">
                        <BackButton to="/admin/product-variations" />
                      </div>
                    </div>
                  </div>

                  <Formik
                    initialValues={ProductIntialValues}
                    validationSchema={ProductValidationSchema}
                    validateOnChange
                    innerRef={formRef}
                    onSubmit={handleSubmitEditProductVariations}
                  >
                    {(formik) => {
                      const {
                        errors,
                        setFieldValue,
                        values,
                      } = formik;

                      return (
                        <Form>
                          <div className="section-border">
                            <div className="row">
                              <div className="col-md-6">
                                <Field
                                  Label="Name"
                                  type="text"
                                  name="name"
                                  placeholder="Enter Name"
                                  component={InputWithLabel}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <FieldArray
                                name="variations"
                                render={(arrayHelpers) => (
                                  <div>
                                    <div className="row">
                                      <div className="col-md-6">
                                        <Field
                                          Label="Variations"
                                          type="text"
                                          name="variationInput"
                                          formGroupClassName="mb-0"
                                          placeholder="Enter Product Variations Name"
                                          component={InputWithLabel}
                                          onChange={(e) =>
                                            setFieldValue(
                                              "variationInput",
                                              e.target.value
                                            )
                                          }
                                          value={values.variationInput}
                                        />
                                        {!values?.variations?.length && (
                                          <ErrorMessage
                                            name="variations"
                                            component="div"
                                            className="text-danger"
                                          />
                                        )}
                                      </div>

                                      <div className="col-md-6">
                                        {values.variations &&
                                          values.variations.length > 0 && (
                                            <div className="variations-container mt-4">
                                              {values.variations.map(
                                                (variation, index) => (
                                                  <span
                                                    key={index}
                                                    className={
                                                      variation.name
                                                        ? "add-varitions-border"
                                                        : ""
                                                    }
                                                  >
                                                    {variation.name}
                                                    {variation.name && (
                                                      <svg
                                                        stroke="currentColor"
                                                        fill="#BFBBBB"
                                                        strokeWidth="0"
                                                        viewBox="0 0 512 512"
                                                        height="20px"
                                                        width="20px"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        style={{
                                                          cursor: "pointer",
                                                          marginLeft: "8px",
                                                        }}
                                                        onClick={() => {
                                                          arrayHelpers.remove(
                                                            index
                                                          );
                                                          setRemoveItem(
                                                            (oldValue) => [
                                                              ...oldValue,
                                                              values
                                                                ?.variations[
                                                                index
                                                              ]?.id,
                                                            ]
                                                          );
                                                        }}
                                                      >
                                                        <path d="M278.6 256l68.2-68.2c6.2-6.2 6.2-16.4 0-22.6-6.2-6.2-16.4-6.2-22.6 0L256 233.4l-68.2-68.2c-6.2-6.2-16.4-6.2-22.6 0-3.1 3.1-4.7 7.2-4.7 11.3 0 4.1 1.6 8.2 4.7 11.3l68.2 68.2-68.2 68.2c-3.1 3.1-4.7 7.2-4.7 11.3 0 4.1 1.6 8.2 4.7 11.3 6.2 6.2 16.4 6.2 22.6 0l68.2-68.2 68.2 68.2c6.2 6.2 16.4 6.2 22.6 0 6.2-6.2 6.2-16.4 0-22.6L278.6 256z"></path>
                                                      </svg>
                                                    )}
                                                  </span>
                                                )
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col-md-6 mt-2">
                                        <button
                                          type="button"
                                          className="add-product-variation-use-button plus-product-variation-button"
                                          disabled={!values.variationInput?.trim()}
                                          onClick={() => {
                                            if (!errors.variationInput) {
                                              arrayHelpers.push({
                                                name: values.variationInput,
                                              });
                                              setFieldValue("variationInput", "");
                                            }
                                          }}
                                        >
                                          <svg
                                            stroke="currentColor"
                                            fill="currentColor"
                                            strokeWidth="0"
                                            viewBox="0 0 448 512"
                                            height="14px"
                                            width="20px"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"></path>
                                          </svg>
                                          Add
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              />
                            </div>
                            <SaveButton loading={isLoading} />
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProductVariations;
